import {
    Component,
    type ElementRef,
    effect,
    input,
    model,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from '@openng/optimus-ui/tooltip';

import { AiModelPickerComponent } from '@app/shared/components/ai-chat/ai-model-picker/ai-model-picker.component';
import type { ChatModelOption } from '@app/shared/models/ai-chat.model';

@Component({
    selector: 'cor-ai-chat-input',
    templateUrl: './ai-chat-input.component.html',
    styleUrls: ['./ai-chat-input.component.scss'],
    imports: [FormsModule, TooltipModule, AiModelPickerComponent],
})
export class AiChatInputComponent {
    readonly disabled = input<boolean>(false);
    readonly isLoading = input<boolean>(false);
    readonly placeholder = input<string>('Type a message...');
    readonly selectedModel = model<ChatModelOption | null>(null);
    readonly send = output<string>();
    readonly abortRequest = output<void>();

    private readonly textareaRef = viewChild.required<ElementRef<HTMLTextAreaElement>>('textarea');

    protected readonly value = signal('');

    private static readonly MIN_HEIGHT_PX = 48;
    private static readonly MAX_HEIGHT_PX = 320;

    constructor() {
        effect(() => {
            this.value();
            queueMicrotask(() => this.autoResize());
        });
    }

    prefill(text: string): void {
        this.value.set(text);
        this.focus();
        queueMicrotask(() => this.autoResize({ scrollToTop: true }));
    }

    focus(): void {
        this.textareaRef().nativeElement.focus();
    }

    clear(): void {
        this.value.set('');
    }

    hasDraft(): boolean {
        return this.value().trim().length > 0;
    }

    protected onKeydown(event: KeyboardEvent): void {
        if (event.key !== 'Enter' || event.shiftKey || event.isComposing) {
            return;
        }
        event.preventDefault();
        this.onSendClick();
    }

    protected onSendClick(): void {
        const text = this.value().trim();
        if (!text || this.disabled()) {
            return;
        }

        this.send.emit(text);
        this.value.set('');

        const el = this.textareaRef().nativeElement;
        el.value = '';
        this.autoResize();
    }

    private autoResize(options: { scrollToTop?: boolean } = {}): void {
        const el = this.textareaRef().nativeElement;
        el.style.height = 'auto';
        const next = Math.min(
            Math.max(el.scrollHeight, AiChatInputComponent.MIN_HEIGHT_PX),
            AiChatInputComponent.MAX_HEIGHT_PX,
        );
        el.style.height = `${next}px`;
        el.style.overflowY =
            el.scrollHeight > AiChatInputComponent.MAX_HEIGHT_PX ? 'auto' : 'hidden';
        if (options.scrollToTop) {
            el.scrollTop = 0;
        }
    }
}
